import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type {
  CollegeProgram,
  CollegeTier,
  CollegeType,
  UserCollege,
  UserCollegeStatus,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { tierLabel } from './components/TierChip';

const { schoolType, program } = appStrings.modules.collegeFilters;

type MyCollegesAgentContext = Readonly<{
  view: 'table' | 'cards';
  statusFilter: UserCollegeStatus | null;
  tierFilter: CollegeTier | null;
  typeFilter: CollegeType | null;
  programFilter: CollegeProgram | null;
  colleges: readonly UserCollege[];
}>;

export const useAgentContext = ({
  view,
  statusFilter,
  tierFilter,
  typeFilter,
  programFilter,
  colleges,
}: MyCollegesAgentContext) => {
  useRegisterAgentContext({
    kind: 'list',
    description: `The colleges on the student's My College List, shown as a ${view === 'table' ? 'table' : 'set of cards'} grouped by Safety / Target / Reach${statusFilter ? `, filtered to status "${statusFilter}"` : ''}${tierFilter ? `, filtered to ${tierLabel(tierFilter)} colleges` : ''}${typeFilter ? `, filtered to ${schoolType.options[typeFilter].toLowerCase()} colleges` : ''}${programFilter ? `, filtered to Program "${program.options[programFilter]}"` : ''}. The School Type filter picks public or private colleges and the Program filter picks 4-year schools, 2-year schools (public community colleges) or trade schools (private 2-year schools); both work together with the Status and Level filters. A search at the top adds more schools to the list. Tier is Backrs' rating for this student; "Not rated" means it could not be rated. A null admissions fact is one Backrs does not have. net_cost is per year: from the student's saved award letter when net_cost_source is "award_letter", a College Scorecard estimate for families at their income when it is "scorecard", or the college's average for all aided students when it is "average" (the student has no family income on their profile, or the college has no price for it).`,
    data: colleges.map((college) => ({
      name: college.name,
      tier: tierLabel(college.tier),
      type: college.type && schoolType.options[college.type],
      program: college.level && program.options[college.level],
      status: college.status,
      application_plan: college.application_plan,
      my_deadline: college.my_deadline,
      rolling_admission: college.rolling_admission,
      early_action: college.early_action,
      early_action_restrictive: college.early_action_restrictive,
      early_action_deadline: college.early_action_deadline,
      test_optional: college.test_optional,
      test_blind: college.test_blind,
      essay_requirement: college.essay_requirement,
      recommendation_requirement: college.recommendation_requirement,
      interview_requirement: college.interview_requirement,
      net_cost: college.net_cost,
      net_cost_source: college.net_cost_source,
      has_award_letter: college.has_award_letter,
    })),
  });
};
